import React, { FC, useRef, useEffect, useCallback, useState } from 'react';
import Slider from 'react-slick';
import { InferProps } from 'prop-types';

import './BarCarousel.scss';

const BarCarouselPropType = {};
type BarCarouselProps = InferProps<typeof BarCarouselPropType>;

const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  border: 0,
  padding: 0,
  clip: 'rect(0 0 0 0)',
  overflow: 'hidden',
  whiteSpace: 'nowrap'
};

const BarCarousel: FC<BarCarouselProps> = props => {
  const { children } = props;
  const validChildren: JSX.Element[] = [];
  const myCarouselItemsRef = useRef(null)

  React.Children.forEach(children as any, (child, index) => {
    const { props: { id = '' } = {} } = child || {};
    if (id !== 'ignoreComponent') {
      validChildren.push(<div key={index}>{child}</div>);
    }
  });

  const sliderRef = useRef<any>(null);
  const intervalId = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  // start/stop autoplay via setInterval (keeps your original approach)
  const startAuto = useCallback(() => {
    if (intervalId.current) clearInterval(intervalId.current);
    intervalId.current = setInterval(() => {
      sliderRef.current?.slickNext();
    }, 3000);
  }, []);

  const stopAuto = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
  }, []);

  useEffect(() => {
    if (isPlaying) startAuto();
    else stopAuto();
    return () => stopAuto();
  }, [isPlaying, startAuto, stopAuto]);

  useEffect(() => {
    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
    };
  }, []);

    useEffect(() => {
      try {
        const timer = setTimeout(() => {
          const dots = myCarouselItemsRef.current?.querySelector('.slick-bar-dots');
          if (dots) {
            dots.setAttribute('aria-hidden', 'true');
          }
        }, 100);
  
        return () => {
          clearTimeout(timer)
        };
  
      } catch (error) {
        console.log(error)
      }
    }, []);

  const onRefChange = useCallback((node: any) => {
    if (node !== null) {
      sliderRef.current = node;
      // kick off autoplay if we mounted while playing
      if (isPlaying) startAuto();
    }
  }, [isPlaying, startAuto]);

  const handlePrev = () => sliderRef.current?.slickPrev();
  const handleNext = () => sliderRef.current?.slickNext();
  const handleTogglePlay = () => setIsPlaying(p => !p);

  if (validChildren.length > 1) {
    const slickSettings = {
      dots: true,                 // render dots so layout stays stable…
      arrows: false,
      infinite: true,
      swipe: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      className: 'slick-bar',
      dotsClass: 'slick-bar-dots',
      // …but hide them from SRs and keyboard focus:
      appendDots: (dots: React.ReactNode) => (
        <div
          className="slick-bar-dots"
          aria-hidden="true"
          style={{ display: 'block' }}
        >
          {dots}
        </div>
      ),
      customPaging: () => (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          aria-label=""         // empty label so it’s not discoverable by SRs
        />
      ),
    };

    return (
      <div className="bar-carousel" aria-roledescription="carousel"
        ref={myCarouselItemsRef}>
        <Slider {...slickSettings} ref={onRefChange}>
          {validChildren.map(ele => ele)}
        </Slider>

        {/* SR-only controls (visually hidden but operable by SR/keyboard) */}
        <div
          role="toolbar"
          aria-label="Controls for App Install / Open Carousel"
          style={srOnlyStyle}
        >
          <button
            type="button"
            onClick={handlePrev}
            aria-label="Previous slide"
            style={srOnlyStyle}
          >
            Previous
          </button>
          <button
            type="button"
            onClick={handleTogglePlay}
            aria-label={isPlaying ? 'Pause carousel' : 'Play carousel'}
            aria-pressed={!isPlaying}
            style={srOnlyStyle}
          >
            {isPlaying ? 'Pause' : 'Play'}
          </button>
          <button
            type="button"
            onClick={handleNext}
            aria-label="Next slide"
            style={srOnlyStyle}
          >
            Next
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

BarCarousel.propTypes = BarCarouselPropType;
export default BarCarousel;
